import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Link2, Flag, ExternalLink } from "lucide-react";
import PublicClipRemoveButton from "../components/core-components/PublicClipRemoveButton";
import {
  addDoc,
  doc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";

import { auth, db } from "../Security/firebase-config";
import { feedbackReportsCollection } from "../utils/communityFirestorePaths";
import "./PublicClips.css";

function normalizeText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeDateValue(value) {
  if (!value) return null;
  if (typeof value === "string") {
    return Number.isFinite(Date.parse(value)) ? new Date(value).toISOString() : null;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return new Date(value).toISOString();
  }
  if (typeof value?.toDate === "function") {
    try {
      return value.toDate().toISOString();
    } catch {
      return null;
    }
  }
  if (typeof value?.seconds === "number") {
    return new Date(value.seconds * 1000).toISOString();
  }
  return null;
}

function formatDateLabel(value) {
  const normalized = normalizeDateValue(value);
  if (!normalized) return "Recently published";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(normalized));
}

function formatDurationLabel(durationMs) {
  const totalSeconds = Math.max(0, Math.round(Number(durationMs || 0) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function coercePublicClip(raw, docId) {
  const clipId = normalizeText(raw?.clipId) || docId;
  const title = normalizeText(raw?.title);
  const publicVideoUrl = normalizeText(raw?.publicVideoUrl);
  const status = normalizeText(raw?.status) || "published";
  const visibility = normalizeText(raw?.visibility) || "public";

  if (!clipId || !title || !publicVideoUrl || status !== "published" || visibility !== "public") {
    return null;
  }

  return {
    clipId,
    title,
    description: normalizeText(raw?.description),
    ownerUid: normalizeText(raw?.ownerUid),
    ownerUsername: normalizeText(raw?.ownerUsername) || "Unknown",
    publicVideoUrl,
    publicPosterUrl: normalizeText(raw?.publicPosterUrl),
    durationMs: Math.max(0, Number(raw?.durationMs || 0)),
    shareUrl: normalizeText(raw?.shareUrl),
    publishedAt: normalizeDateValue(raw?.publishedAt),
  };
}

export default function PublicClipDetailPage() {
  const { clipId } = useParams();
  const [clip, setClip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [reporting, setReporting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadClip() {
      setLoading(true);
      setFeedback(null);

      try {
        const clipSnapshot = await getDoc(doc(db, "publicClips", clipId));
        if (cancelled) return;

        if (!clipSnapshot.exists()) {
          setClip(null);
          return;
        }

        setClip(coercePublicClip(clipSnapshot.data() || {}, clipSnapshot.id));
      } catch (error) {
        if (cancelled) return;
        setFeedback({
          tone: "error",
          message: error instanceof Error ? error.message : "This public clip could not be loaded.",
        });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    if (clipId) {
      void loadClip();
    } else {
      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [clipId]);

  async function handleShareLink() {
    const targetUrl = clip?.shareUrl || window.location.href;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(targetUrl);
      } else {
        const element = document.createElement("textarea");
        element.value = targetUrl;
        element.setAttribute("readonly", "true");
        element.style.position = "fixed";
        element.style.opacity = "0";
        document.body.appendChild(element);
        element.select();
        document.execCommand("copy");
        document.body.removeChild(element);
      }

      setFeedback({
        tone: "success",
        message: "Public clip link copied.",
      });
    } catch (error) {
      setFeedback({
        tone: "error",
        message:
          error instanceof Error ? error.message : "The public clip link could not be copied.",
      });
    }
  }

  async function handleReportClip() {
    if (!clip) return;
    if (!auth.currentUser) {
      setFeedback({
        tone: "error",
        message: "Sign in before reporting public clips.",
      });
      return;
    }

    const details = window.prompt(
      "Why are you reporting this clip? Add a short note for the moderation team.",
    );

    if (!details || !details.trim()) {
      return;
    }

    setReporting(true);
    try {
      const reporterId = auth.currentUser.uid;
      let reporterUsername = "Unknown";

      try {
        const userSnapshot = await getDoc(doc(db, "users", reporterId));
        if (userSnapshot.exists()) {
          reporterUsername = userSnapshot.data()?.username || "Unknown";
        }
      } catch {
        // Optional enrichment.
      }

      await addDoc(feedbackReportsCollection(db), {
        status: "submitted",
        createdAt: serverTimestamp(),
        reporterId,
        reporterUsername,
        postId: clip.clipId,
        postType: "publicClip",
        postOwnerId: clip.ownerUid || null,
        postTeamId: null,
        postDisplayName: clip.title,
        reason: "Public clip report",
        otherText: details.trim().slice(0, 200),
        shareUrl: clip.shareUrl || window.location.href,
      });

      setFeedback({
        tone: "success",
        message: "Clip report submitted.",
      });
    } catch (error) {
      setFeedback({
        tone: "error",
        message:
          error instanceof Error ? error.message : "The public clip report could not be submitted.",
      });
    } finally {
      setReporting(false);
    }
  }

  return (
    <div className="public-clip-detail-page">
      <div className="public-clips-shell">
        <div className="public-clip-detail-header">
          <Link to="/clips" className="public-clip-detail-back">
            Back to all public clips
          </Link>
          <h1>{clip?.title || "Public clip"}</h1>
          {clip ? (
            <div className="public-clip-detail-meta">
              <span>{clip.ownerUsername}</span>
              <span>{formatDateLabel(clip.publishedAt)}</span>
              <span>{formatDurationLabel(clip.durationMs)}</span>
            </div>
          ) : null}
        </div>

        {feedback ? (
          <div
            className={`public-clips-feedback ${
              feedback.tone === "error" ? "is-error" : "is-success"
            }`}
          >
            {feedback.message}
          </div>
        ) : null}

        {loading ? <div className="public-clips-empty">Loading clip...</div> : null}

        {!loading && !clip ? (
          <div className="public-clips-empty">
            That public clip could not be found or is no longer available.
          </div>
        ) : null}

        {!loading && clip ? (
          <section className="public-clip-detail-surface">
            <video
              className="public-clip-detail-video"
              controls
              playsInline
              poster={clip.publicPosterUrl || undefined}
              src={clip.publicVideoUrl}
            />

            <div className="public-clip-detail-actions">
              <button
                type="button"
                className="public-clips-browser__icon-button"
                aria-label="Share link" title="Share link"
                onClick={() => void handleShareLink()}
              >
                <Link2 size={16} aria-hidden="true" />
              </button>
              <button type="button" className="public-clips-browser__icon-button"
                aria-label={reporting ? "Submitting report" : "Report clip"} title="Report clip"
                onClick={() => void handleReportClip()} disabled={reporting}>
                <Flag size={16} aria-hidden="true" />
              </button>
              {clip.publicVideoUrl ? (
                <a className="public-clips-browser__icon-button" aria-label="Open raw clip file" title="Open raw clip file"
                  href={clip.publicVideoUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink size={16} aria-hidden="true" />
                </a>
              ) : null}
              <PublicClipRemoveButton clip={clip} onRemoved={() => setClip(null)} onFeedback={setFeedback} />
            </div>

            {clip.description ? (
              <p className="public-clip-detail-description">{clip.description}</p>
            ) : (
              <p className="public-clip-detail-description">
                No description was added for this public clip.
              </p>
            )}
          </section>
        ) : null}
      </div>
    </div>
  );
}
